<template>
	<div
		id="product-reviews"
		class="product-reviews"
	>
		<template v-if="totalRatings">
			<h2 class="heading">Dette mener kundene våre</h2>
			<div class="average-rating">
				<KeyFigure
					:class="{ loading }"
					:modelValue="averageRating"
					class="key-figure"
				/>
			</div>
			<div
				v-for="(ratingbar, i) in ratingBars"
				:key="i"
				class="secondary-rating"
			>
				<div class="rating-heading">
					<span>{{ ratingbar.Label }}</span>
					<span>{{ ratingbar.AverageRating.toFixed(1) }}</span>
				</div>
				<TnProgressLinear
					:indeterminate="loading"
					:modelValue="ratingbar.AverageRating / 5"
					color="color-primary-telenor"
					height="13"
					rounded
				/>
			</div>
			<WriteReviewModal
				:product-category="eCommerceProduct.category"
				class="margin-top-xl"
			/>

			<div
				ref="root"
				class="padding-top-3xl"
			>
				<div>{{ page * 10 + 1 }} - {{ currentPageRange }} av {{ totalRatings }} Omtaler</div>
				<ProductReview
					v-for="(rating, i) in ratings"
					:key="i"
					:rating="rating"
					class="review"
				/>
			</div>

			<div
				v-if="showPagination && numberOfPages > 1"
				class="pagination margin-top-xl"
			>
				<TnButton
					:disabled="page < 1"
					class="margin-right-auto"
					size="s"
					tertiary
					@click="prevPage"
				>
					<TnIcon name="chevron-left" />
				</TnButton>
				<ul>
					<li
						v-for="(p, key) in numberOfPages"
						:key="key"
					>
						<a
							:class="{ active: currentPage === p }"
							href="javascript:"
							@click="setPage(p - 1)"
							>{{ p }}</a
						>
					</li>
				</ul>
				<TnButton
					:disabled="currentPage >= numberOfPages"
					size="s"
					style="margin-left: auto"
					tertiary
					@click="nextPage"
				>
					<TnIcon name="chevron-right" />
				</TnButton>
			</div>
		</template>
		<template v-else>
			<h2
				v-if="loading"
				class="heading"
			>
				Dette mener kundene våre
			</h2>
			<h2
				v-else
				class="heading"
			>
				Vær den første til å omtale dette produktet
			</h2>
			<div
				v-if="loading"
				class="average-rating"
			>
				<KeyFigure
					:class="{ loading }"
					:modelValue="0"
					class="key-figure"
				/>
			</div>
			<div
				v-for="(ratingbar, i) in ratingBars"
				:key="i"
				class="secondary-rating"
			>
				<div class="rating-heading">
					<span>{{ ratingbar.Label }}</span>
					<span>{{ ratingbar.AverageRating.toFixed(1) }}</span>
				</div>
				<TnProgressLinear
					:indeterminate="loading"
					:modelValue="ratingbar.AverageRating / 5"
				/>
			</div>
		</template>
		<WriteReviewModal
			:productCategory="eCommerceProduct.category"
			class="margin-top-xl"
		/>
	</div>
</template>

<script>
import ECommerceProductMixin from "../../ECommerceProductMixin/ECommerceProductMixin.js";

import BazaarvoiceIntegration from "~/components/ecommerce/ECommerceProductDetails/bazaarvoice-integration";

const ITEMS_PER_PAGE = 10;

const bazaarvoiceIntegration = BazaarvoiceIntegration(ITEMS_PER_PAGE);

export default defineNuxtComponent({
	name: "ProductReviews",
	mixins: [ECommerceProductMixin],

	data() {
		return {
			reviews: {},
			page: 0,
			loading: false,
			writeReviewModal: false,
		};
	},

	methods: {
		async getReviews(isFetch) {
			if (isFetch) this.loading = true;
			this.reviews = await bazaarvoiceIntegration.getReviews(this.eCommerceProduct?.bazaarVoiceId, this.page);
			if (!isFetch) this.$refs.root.scrollIntoView({ behavior: "smooth" });
			this.loading = false;
		},
		nextPage() {
			this.page++;
			this.getReviews();
		},
		prevPage() {
			this.page--;
			this.getReviews();
		},
		setPage(page) {
			this.page = page;
			this.getReviews();
		},
	},

	async mounted() {
		await this.getReviews(true);
	},

	computed: {
		currentPage() {
			return this.page + 1;
		},
		showPagination() {
			return this.totalRatings > ITEMS_PER_PAGE;
		},
		averageRating() {
			if (this.reviews?.Includes?.Products)
				return (
					Number(
						this.reviews?.Includes?.Products[
							Object.keys(this.reviews.Includes.Products)[0]
						]?.ReviewStatistics?.AverageOverallRating?.toFixed(1),
					) || 0.0
				);
			return undefined;
		},
		ratingBars() {
			if (this.reviews?.Includes?.Products) {
				return (
					this.reviews?.Includes?.Products[Object.keys(this.reviews.Includes.Products)[0]]?.ReviewStatistics
						.SecondaryRatingsAverages || {
						PictureQuality: {
							Label: "Kamera",
							AverageRating: 0,
						},
						Skjerm: {
							Label: "Skjerm",
							AverageRating: 0,
						},
						Design: {
							Label: "Design",
							AverageRating: 0,
						},
						BatteryLife: {
							Label: "Batterilevetid",
							AverageRating: 0,
						},
						EaseOfUse: {
							Label: "Enkel i bruk",
							AverageRating: 0,
						},
					}
				);
			} else return undefined;
		},
		totalRatings() {
			return this.reviews?.TotalResults;
		},
		ratings() {
			return this.reviews?.Results;
		},
		numberOfPages() {
			return Math.ceil(this.totalRatings / ITEMS_PER_PAGE);
		},
		currentPageRange() {
			return Math.min((this.page + 1) * ITEMS_PER_PAGE, this.totalRatings);
		},
	},
});
</script>

<style lang="scss" scoped>
.rating-stars {
	vertical-align: bottom;
}

.secondary-rating {
	max-width: 416px;
	margin: $spacing-m auto;
}

.rating-heading {
	display: flex;
	justify-content: space-between;
	margin: $spacing-s 0;
}

.product-reviews {
	h2 {
		text-align: center;
		margin: $spacing-xl 0;

		@include font-title-xl;
	}

	h3 {
		margin: $spacing-l 0;

		@include font-title-l;
	}
}

.average-rating {
	text-align: center;

	.key-figure {
		font-size: 52px;
		color: #19aaf8;
		font-weight: bold;
		transition: color 0.3s;

		&.loading {
			color: $color-neutrals-200-tint;
		}
	}
}

.pagination {
	display: flex;

	ul {
		display: inline-block;
		margin-left: auto;
		align-self: center;
		white-space: nowrap;
		overflow: hidden;
		padding: 0 $spacing-m;

		li {
			display: inline-block;

			a {
				display: inline-block;
				text-align: center;
				width: $spacing-xl;
				height: $spacing-xl;
				line-height: $spacing-xl;
				text-decoration: none;
				color: inherit;
				font-weight: bold;
				border-radius: 7px;
				transition: background-color 0.2s ease;

				&:hover {
					background: $color-neutrals-25-tint;
				}

				&.active {
					background: $color-background-blue-light;
				}
			}
		}
	}
}
</style>
